import React, { useEffect } from "react";
import { API_SUCCESS } from "../../../common/constants/ApiConstants";
import { DISABILITY_OR_SOCIAL_SECURITY } from "../../../models/requestType";
import { RequestorFormConstants } from "./DataEntryFieldsConstants";
import { Requestor } from "../../../models/requestor";
import { RequestPurpose } from "../../../models/requestType";

export interface DataEntryDetailsRightPaneRequestorSectionProps {
    readonly activeTabId: number;
    readonly requestType?: number;
    readonly canEditPatient?: boolean;
    readonly noBarcodeValue?: boolean;
    readonly transactionDdsSsn?: any;
    readonly ssnValue?: string;
    readonly requestorDetails?: Requestor;
    readonly requestPurpose?: RequestPurpose;
    readonly organizationId?: number;
    readonly transactionId?: string;
    readonly transactionDDS?: any;
    readonly readBarCodeDDSAPIStatus?: any;
    readonly changeFieldValue: (fieldName: string, value: any) => void;
    readonly setDisableField: (value: boolean) => void;
    readonly setDisableDDSField: (value: boolean) => void;
    readonly setLastUsedRequestorInfo: (data: any) => void;
    readonly setErrorMessage: (value: string) => void;
    readonly setDOBError: (value: string) => void;
    readonly setLastNameError: (value: string) => void;
}

/**
 * Requestor-tab-scoped effects extracted from DataEntryDetailsRightPane.tsx
 * (Spec #34098 / F1a, AC13, Business Rule 16 - tidy-first structural refactor
 * #1). Headless by design: state ownership stays with the RightPane
 * orchestrator (passed down as props/setters) so behavior is unchanged -
 * only the *location* of these effects moves. Always rendered by the
 * orchestrator regardless of activeTabId, matching the pre-refactor firing
 * semantics.
 */
const DataEntryDetailsRightPaneRequestorSection: React.FC<DataEntryDetailsRightPaneRequestorSectionProps> = (props) => {
    useEffect(() => {
        props.setDisableField(!props.canEditPatient);
    }, [props.canEditPatient]);

    useEffect(() => {
        if (!props.noBarcodeValue && props.transactionDdsSsn) {
            if (props.ssnValue) {
                props.changeFieldValue(RequestorFormConstants.SSN, '***-**-***');
            }
        }
    }, [props.activeTabId === 2 && props.requestType == DISABILITY_OR_SOCIAL_SECURITY]);

    useEffect(() => {
        if (props.requestorDetails?.requestorInfo?.id) {
            props.setLastUsedRequestorInfo({
                requestorName: props.requestorDetails?.fullName,
                address1: props.requestorDetails?.requestorAddress?.address1,
                address2: props.requestorDetails?.requestorAddress?.address2,
                city: props.requestorDetails?.requestorAddress?.city,
                state: props.requestorDetails?.requestorAddress?.state,
                zip: props.requestorDetails?.requestorAddress?.zipCode,
                firstName: props.requestorDetails?.requestorInfo?.firstName,
                lastName: props.requestorDetails?.requestorInfo?.lastName,
                email: props.requestorDetails?.requestorInfo?.email,
                phone: props.requestorDetails?.requestorInfo?.phone,
                fax: props.requestorDetails?.requestorInfo?.fax,
                requestorContactId: props.requestorDetails?.requestorInfo?.id,
                requestorId: props.requestorDetails?.requestorID,
                requestPurpose: props.requestPurpose,
                org: props.organizationId,
                transactionId: props.transactionId,
            });
        }
    }, [props.requestorDetails?.requestorInfo?.id]);

    useEffect(() => {
        if (props.transactionDDS?.rqid) {
            props.changeFieldValue(RequestorFormConstants.RQID, props.transactionDDS?.rqid);
        }
        if (props.transactionDDS?.txSiteCode) {
            props.changeFieldValue(RequestorFormConstants.TX_SITE_CODE, props.transactionDDS?.txSiteCode);
            props.changeFieldValue(RequestorFormConstants.RQ_SITE_CODE, props.transactionDDS?.txSiteCode);
        }
        if (props.transactionDDS?.docReturnCode) {
            props.changeFieldValue(RequestorFormConstants.DOC_RETURN_CODE, props.transactionDDS?.docReturnCode);
        }
        if (props.transactionDDS?.ssn) {
            props.changeFieldValue(RequestorFormConstants.SSN, '***-**-****');
        }
        if (props.transactionDDS?.docType) {
            props.changeFieldValue(RequestorFormConstants.DOC_TYPE, props.transactionDDS?.docType);
        }
        if (props.transactionDDS?.routingField) {
            props.changeFieldValue(RequestorFormConstants.ROUTING_FIELD, props.transactionDDS?.routingField);
        }
    }, [props.transactionDDS, props.readBarCodeDDSAPIStatus == API_SUCCESS]);

    useEffect(() => {
        props.setDisableDDSField(Boolean(props.noBarcodeValue));
    }, [props.noBarcodeValue]);

    // Additive: mirrors the search-error-reset portion of the pre-refactor
    // orchestrator-level [activeTabId] effect (RightPane.tsx) for this
    // section's own error state (Test 5.5). The orchestrator's original
    // effect is left in place (Business Rule 16 only names the 3
    // cross-cutting effects as staying there; this reset is idempotent so
    // running it from both places is behavior-preserving).
    useEffect(() => {
        props.setErrorMessage("");
        props.setDOBError("");
        props.setLastNameError("");
    }, [props.activeTabId]);

    return null;
};

export default DataEntryDetailsRightPaneRequestorSection;
